import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cellAnswerRequest } from "@/lib/cellAnswer";
import { queueRowSave, rowSaveKey } from "@/lib/rowSaveQueue";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Switch } from "@/components/ui/switch";
import { LocationBoxPanel } from "@/components/interview/LocationBox";
import { FullTextCell, FullTextPanel } from "@/components/interview/FullText";
import { saveSettled, useSaveFailure } from "@/components/interview/saveFailure";
import { useSectionFields } from "@/components/interview/useSectionFields";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { HOME_BOARD, RULED_EXECUTOR_WORDS, executorNoPageSub, fill } from "./planBoard";
import { PlanSearch } from "./PlanSearch";
import { addKinds, filterRows, groupRows, landsOn, screenKeyOf, whatAndWhich, type WhereRow } from "./whereThings";

/**
 * s237 (ACROSS-YOUR-PLAN-1 stage 1, PR B; D-236.50-30, D-237.33) — WHERE THINGS ARE, the planner's view (board 12
 * :1105–1243). Every place the plan keeps, in one list grouped by the page it was asked on, with search and "Only what
 * is missing". Any row is answered here in the writing box and saves to the same cell, through the same request, as
 * its own screen (lib/cellAnswer.ts). "+ Add" asks what it is first and opens that thing's screen ("← Where things
 * are"); a thing with no page is kept here. The words are board 12's (`HOME_BOARD.where`, RULE 137).
 *
 * s237 PR D part 1 (D-236.50-25(b), D-223.30, D-237.35, D-237.36 / .50, D-237.55) — THE EXECUTOR'S VIEW is this same page
 * (`executor`), after a verified death: the same list, search, "Only what is missing" and "+ Add". A blank reads
 * "{planner} did not say" (board 12, "Sarah, after"). Every save goes through the executor's ONE fact-edit writer
 * (`PATCH /api/executor/:id/entry` → writeExecutorEntryChange: one change-log row per change); a new thing is the
 * executor's add (`POST /api/executor/:id/row` → addExecutorRow: one add_row change). The executor has no copy of the
 * interview's screens (their category pages are read-only), so every "+ Add" kind keeps its thing HERE, in Things on no
 * page, and no row offers "Open the page instead".
 */
const W = HOME_BOARD.where;
/** Board 12's grid: What · Which one · Where it is · the action. */
const GRID = "minmax(0, 1.55fr) minmax(0, 1fr) minmax(0, 1.6fr) 120px";
const PARTS = ["what", "trail", "which", "place"] as const;
const rowKey = (r: WhereRow) => `${r.sectionId}|${r.rowId ?? ""}|${r.fieldId}`;
export const whereKey = (clientId: string) => ["/api/clients", clientId, "where-things-are"] as const;

export function WherePage({ clientId, onOpen, executor }: {
  clientId: string;
  onOpen: (screenKey: string) => void;
  /** s237: the executor's view — the planner's first name for the blanks; saves go through the executor's writer. */
  executor?: { planner: string };
}) {
  const { data, isSuccess } = useQuery<{ rows: WhereRow[] }>({
    queryKey: whereKey(clientId),
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/where-things-are`)).json(),
  });
  const [query, setQuery] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [kindOpen, setKindOpen] = useState(false);
  const [newThing, setNewThing] = useState<{ item: string } | null>(null);

  const onSaveFailed = useSaveFailure();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: whereKey(clientId) });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
  };
  // The cell (or the typed record and its cells) upserts by row; one row's saves queue (rowSaveQueue).
  const saveMutation = useMutation({
    mutationFn: async ({ row, value }: { row: WhereRow; value: string }) => {
      if (executor) {
        // D-237.35: the executor's change, recorded — the one fact-edit writer, keyed by the cell's coordinates.
        return queueRowSave(rowSaveKey(clientId, row.sectionId, row.rowId), () => apiRequest("PATCH", `/api/executor/${clientId}/entry`, {
          section: row.sectionSlug, rowId: row.rowId, fieldId: row.fieldId, fieldKey: row.fieldKey, newValue: value,
        }));
      }
      const r = cellAnswerRequest(clientId, row, row.fieldId, value);
      return queueRowSave(rowSaveKey(clientId, row.sectionId, row.rowId), () => apiRequest("POST", r.url, r.body));
    },
    onError: onSaveFailed,
  });
  // ONE lock on the answer box (the single-flight rule): a second Done while the first is still out waits for none.
  const saveFlight = useSingleFlight();
  const save = (row: WhereRow, value: string) =>
    saveFlight.run(() => saveSettled(saveMutation.mutateAsync({ row, value }))).then((ok) => { if (ok) { setEditing(null); refresh(); } return !!ok; });

  // Things on no page: the plan's own catch-all list (Where Are They?) — a new row is a new record. Class A: a second
  // fire would be a second thing, so ONE lock, released on the promise.
  const noPage = useSectionFields(clientId, CATEGORY_SLUGS.MY_WISHES_AND_LEGACY, SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY);
  const addFlight = useSingleFlight();
  const addMutation = useMutation({
    mutationFn: async ({ item, place }: { item: string; place: string }) => {
      const itemId = noPage.fieldIdByKey.get("item");
      const placeId = noPage.fieldIdByKey.get("location");
      if (!noPage.sectionId || !itemId || !placeId) throw new Error("Things on no page is not ready yet.");
      // D-237.36 / .50: the executor adds a place too — the executor's add (one add_row change, D-237.35).
      if (executor) return apiRequest("POST", `/api/executor/${clientId}/row`, {
        section: SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY,
        fields: [{ fieldId: itemId, fieldKey: "item", value: item }, { fieldId: placeId, fieldKey: "location", value: place }],
      });
      return apiRequest("POST", `/api/clients/${clientId}/typed-rows`, {
        categoryId: noPage.categoryId, sectionId: noPage.sectionId, rowId: crypto.randomUUID(),
        cells: [{ fieldId: itemId, value: item }, { fieldId: placeId, value: place }],
      });
    },
    onError: onSaveFailed,
  });
  const keepHere = (place: string) => {
    const item = (newThing?.item ?? "").trim() || W.newThing;
    return addFlight.run(() => saveSettled(addMutation.mutateAsync({ item, place }))).then((ok) => { if (ok) { setNewThing(null); refresh(); } return ok; });
  };

  const rows = data?.rows ?? [];
  const shown = filterRows(rows, { query, missingOnly });
  const groups = groupRows(shown);
  // Rule 133(a): a text that does not fit shows ONE line with the expand control, and opens in full under the row.
  const cell = (key: string, part: (typeof PARTS)[number], text: string, cls: string) => (
    <FullTextCell text={text} open={!!open[`${key}:${part}`]} onToggle={() => setOpen((o) => ({ ...o, [`${key}:${part}`]: !o[`${key}:${part}`] }))}
      textClassName={cls} title={W.showAll} testId={`where-${part}`} />
  );

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3.5 px-4 pb-16 pt-6 sm:px-8" data-testid="where-page">
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] text-warm-muted" data-testid="where-from">{W.from}</span>
        <h1 className="font-serif text-[34px] font-normal leading-tight text-warm-charcoal" data-testid="where-title">{W.title}</h1>
        <p className="text-[14.5px] leading-normal text-warm-charcoal" data-testid="where-note">{executor ? fill(RULED_EXECUTOR_WORDS.whereNote, { planner: executor.planner }) : W.note}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <PlanSearch value={query} onChange={setQuery} placeholder={W.search} testId="where-search" />
        <label className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-[8px] border border-warm-stone bg-warm-white px-3.5 text-[14px] text-warm-charcoal">
          <Switch checked={missingOnly} onCheckedChange={setMissingOnly} data-testid="where-missing-only" />
          <span>{W.gapsSwitch}</span>
        </label>
      </div>

      {groups.length > 0 && (
        <div className="overflow-hidden rounded-[12px] border border-warm-stone bg-warm-white" data-testid="where-table">
          <div className="grid gap-3.5 bg-warm-stone-light px-5 py-3 text-[14.5px] font-semibold text-warm-charcoal" style={{ gridTemplateColumns: GRID }}>
            <span>{W.heads[0]}</span><span>{W.heads[1]}</span><span>{W.heads[2]}</span><span />
          </div>
          {groups.map((g) => g.rows.map((r) => {
            const key = rowKey(r);
            const { what, which } = whatAndWhich(r);
            const isEditing = editing === key;
            const screen = screenKeyOf(r);
            const act = r.catA ? "" : isEditing ? W.act.close : r.missing ? W.act.answer : W.act.change;
            return (
              <div key={key} className="border-t border-warm-stone" data-testid={`where-row-${key}`}>
                <div className="grid min-h-11 items-center gap-3.5 px-5" style={{ gridTemplateColumns: GRID }}>
                  {/* D-237.61 (canvas v471): the item and its trail share ONE line. Reade (s237): they STAY on one line — a row
                      keeps one height (rule 133(a)) — and a text that does not fit is cut with the expand control, never dropped
                      under the name (v471 drew it dropping; reported for the board). D-237.62: the trail is plain text. */}
                  <div className="flex min-w-0 flex-nowrap items-baseline gap-x-2.5 py-1">
                    <div className="min-w-0 shrink">{cell(key, "what", what, "line-clamp-1 break-all text-[16px] text-warm-charcoal")}</div>
                    <div className="min-w-0 shrink-[2]">{cell(key, "trail", g.trail, "line-clamp-1 break-all text-[13.5px] text-warm-muted")}</div>
                  </div>
                  <div className="min-w-0">{cell(key, "which", which, "line-clamp-1 break-words text-[15px] text-warm-muted")}</div>
                  {r.location ? (
                    <div className="min-w-0">{cell(key, "place", r.location, "line-clamp-1 break-words text-[15.5px] text-warm-charcoal")}</div>
                  ) : (
                    <span className="text-[14.5px] text-clay-600" data-testid="where-missing">{executor ? fill(W.exNotSaid, { planner: executor.planner }) : W.notAnswered}</span>
                  )}
                  {act ? (
                    <button type="button" onClick={() => setEditing(isEditing ? null : key)}
                      className="min-h-11 justify-self-end text-[14px] text-sage-800 hover:underline" data-testid="where-act">{act}</button>
                  ) : <span />}
                </div>
                {!isEditing && PARTS.some((p) => open[`${key}:${p}`]) && (
                  <div className="px-5">
                    {open[`${key}:what`] && <FullTextPanel label={`${W.heads[0]}:`} text={what} testId="where-what" />}
                    {open[`${key}:trail`] && <FullTextPanel label="" text={g.trail} testId="where-trail" />}
                    {open[`${key}:which`] && <FullTextPanel label={`${W.heads[1]}:`} text={which} testId="where-which" />}
                    {open[`${key}:place`] && r.location && <FullTextPanel label={`${W.heads[2]}:`} text={r.location} testId="where-place" />}
                  </div>
                )}
                {isEditing && (
                  <div className="flex flex-col gap-2 px-5 pb-4">
                    <LocationBoxPanel value={r.location ?? ""} title={what} placeholder={W.boxPrompt}
                      onCommit={(v) => save(r, v)} onClose={() => setEditing(null)} testId={`where-box-${key}`} />
                    <div className="flex flex-wrap items-center gap-3 text-[13px]">
                      <span className="text-warm-muted" data-testid="where-lands-on">{landsOn(r, executor)}</span>
                      {screen && !r.onNoPage && !executor && (
                        <button type="button" onClick={() => onOpen(screen)} className="text-sage-800 hover:underline" data-testid="where-open-page">{W.openPage}</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }))}
        </div>
      )}
      {isSuccess && groups.length === 0 && <span className="text-[14.5px] text-warm-muted" data-testid="where-empty">{W.empty}</span>}

      <button type="button" onClick={() => { setAdding((a) => !a); setEditing(null); }}
        className="min-h-11 self-start rounded-[8px] border border-dashed border-warm-stone px-4 text-[14px] text-sage-800 hover:bg-warm-stone-light" data-testid="where-add">{W.add.open}</button>
      {adding && (
        <div className="flex flex-col gap-4 rounded-[12px] border border-warm-stone bg-warm-white p-5" data-testid="where-add-panel">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[16px] text-warm-charcoal">{W.add.title}</span>
              <span className="text-[14px] text-warm-muted">{W.add.line}</span>
            </div>
            <button type="button" onClick={() => setAdding(false)} className="min-h-11 text-[14px] text-sage-800 hover:underline" data-testid="where-add-close">{W.add.close}</button>
          </div>
          {/* D-237.55: ONE dropdown of kinds of thing, each showing where it goes (board 12, canvas v467). */}
          <div className="flex w-[360px] max-w-full flex-col gap-1.5 border-t border-warm-stone-light pt-3">
            <span className="text-[13px] text-warm-muted" id="where-kind-label">{W.add.label}</span>
            <button type="button" onClick={() => setKindOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={kindOpen} aria-labelledby="where-kind-label"
              className="flex min-h-11 items-center justify-between rounded-[8px] border border-warm-stone bg-warm-white px-3 text-[14px] text-warm-charcoal" data-testid="where-kind">
              <span>{W.add.choose}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-warm-muted"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            {kindOpen && (
              <div role="listbox" aria-labelledby="where-kind-label" className="flex flex-col overflow-hidden rounded-[8px] border border-warm-stone bg-white" data-testid="where-kinds">
                {addKinds().map((k) => ({ ...k, ...(executor ? { screenKey: undefined, where: W.add.addedHere } : {}) })).map((k) => (
                  <button key={k.label} type="button" role="option" aria-selected={false}
                    onClick={() => { setKindOpen(false); setAdding(false); if (k.screenKey) onOpen(k.screenKey); else setNewThing({ item: k.label }); }}
                    className="flex min-h-11 items-center justify-between gap-3 px-3 text-left text-[14px] text-warm-charcoal hover:bg-warm-stone-light"
                    data-testid={`where-kind-${k.label}`}>
                    <span>{k.label}</span><span className="text-[13px] text-warm-muted">{k.where}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {newThing && (
        <div className="flex flex-col gap-2 rounded-[12px] border border-warm-stone bg-warm-white p-5" data-testid="where-new-thing">
          <span className="text-[13px] text-warm-muted" data-testid="where-no-page-line">{W.noPage.name} — {executor ? executorNoPageSub(executor.planner) : W.noPage.sub}</span>
          <input value={newThing.item} onChange={(e) => setNewThing({ item: e.target.value })} placeholder={W.newThing} aria-label={W.newThing}
            className="min-h-11 rounded-[8px] border border-warm-stone bg-warm-white px-3.5 text-[14.5px] text-warm-charcoal" data-testid="where-new-item" />
          <LocationBoxPanel value="" title={(newThing.item.trim() || W.newThing)} placeholder={W.boxPrompt}
            onCommit={(v) => keepHere(v)} onClose={() => setNewThing(null)} testId="where-new-place" />
          <span className="text-[13px] text-warm-muted" data-testid="where-kept-here">{executor ? fill(RULED_EXECUTOR_WORDS.keptHere, { planner: executor.planner }) : W.keptHere}</span>
        </div>
      )}
    </div>
  );
}

